/**
 * Pure, React-free helpers for the Engineering Timeline domain: explicit-event
 * id generation, blank/edit draft factories, validation, kind presentation
 * (labels + badge tones), and date formatting.
 *
 * Deliberately NO productivity/velocity/leaderboard metric.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import type {
  TimelineEvent,
  TimelineEventDraft,
  TimelineEventKind,
} from './types';

/** Deterministic document id for a new explicit timeline event. */
export function timelineEventDocId(): string {
  return `tle_${uuid()}`;
}

/** Today's local date as yyyy-mm-dd. */
export function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The kinds a human may pick for an explicit event. */
export const EXPLICIT_EVENT_KINDS: { value: TimelineEventKind; label: string }[] = [
  { value: 'custom', label: 'Note' },
  { value: 'milestone', label: 'Milestone' },
];

/** Creates an empty draft for the explicit-event form (optionally seeding fields). */
export function blankTimelineEventDraft(
  seed: Partial<TimelineEventDraft> = {},
): TimelineEventDraft {
  return {
    title: '',
    description: undefined,
    date: todayIso(),
    kind: 'custom',
    engineer: undefined,
    relatedGoalId: undefined,
    relatedWorkstreamId: undefined,
    relatedServiceId: undefined,
    relatedDocId: undefined,
    relatedDecisionId: undefined,
    relatedStoryId: undefined,
    ...seed,
  };
}

/** Projects an existing explicit event into an editable draft. */
export function draftFromTimelineEvent(event: TimelineEvent): TimelineEventDraft {
  return {
    title: event.title,
    description: event.description,
    date: event.date,
    kind: event.kind,
    engineer: event.engineer,
    relatedGoalId: event.relatedGoalId,
    relatedWorkstreamId: event.relatedWorkstreamId,
    relatedServiceId: event.relatedServiceId,
    relatedDocId: event.relatedDocId,
    relatedDecisionId: event.relatedDecisionId,
    relatedStoryId: event.relatedStoryId,
  };
}

/** Validates an explicit-event draft for save. */
export function validateTimelineEventDraft(draft: TimelineEventDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('An event title is required.');
  if (!draft.date.trim()) problems.push('An event date is required.');
  return problems;
}

/** Human-readable label for an event kind. */
export function kindLabel(kind: TimelineEventKind): string {
  switch (kind) {
    case 'workstream_started':
      return 'Workstream';
    case 'document_created':
      return 'Doc created';
    case 'document_approved':
      return 'Doc approved';
    case 'decision_made':
      return 'Decision';
    case 'blocker_raised':
      return 'Blocker raised';
    case 'blocker_resolved':
      return 'Blocker resolved';
    case 'milestone':
      return 'Milestone';
    case 'custom':
    default:
      return 'Note';
  }
}

/** Badge tone for an event kind. */
export function kindTone(kind: TimelineEventKind): BadgeTone {
  switch (kind) {
    case 'workstream_started':
      return 'info';
    case 'document_created':
      return 'neutral';
    case 'document_approved':
      return 'success';
    case 'decision_made':
      return 'accent';
    case 'blocker_raised':
      return 'danger';
    case 'blocker_resolved':
      return 'success';
    case 'milestone':
      return 'warning';
    case 'custom':
    default:
      return 'neutral';
  }
}

/** Formats a yyyy-mm-dd (or ISO) date as a compact local date. */
export function formatDate(date?: string): string {
  if (!date) return '';
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
